import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/formatDate';
import { LogOut, RefreshCw, X, CheckCircle, AlertCircle, Shield, Clock } from 'lucide-react';

const Toast = ({ message, type = 'info', onClose }) => (
  <div className={`toast toast-${type}`}>
    {type === 'success' && <CheckCircle size={14} style={{ color: 'var(--green)', flexShrink: 0 }} />}
    {type === 'error'   && <AlertCircle size={14} style={{ color: 'var(--red)',   flexShrink: 0 }} />}
    <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-1)' }}>{message}</span>
    <button className="btn-icon" style={{ width: '24px', height: '24px', border: 'none' }} onClick={onClose}>
      <X size={12} />
    </button>
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    active:  { bg: 'rgba(34,197,94,0.15)',  fg: 'var(--green)', label: 'Активна'  },
    revoked: { bg: 'rgba(239,68,68,0.15)',  fg: 'var(--red)',   label: 'Отозвана' },
    expired: { bg: 'rgba(148,163,184,0.18)', fg: 'var(--text-3)', label: 'Истекла' },
  }[status] || { bg: 'rgba(148,163,184,0.18)', fg: 'var(--text-3)', label: status };

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '2px 8px', borderRadius: '6px',
      background: styles.bg, color: styles.fg,
      fontSize: '12px', fontWeight: 600,
    }}>
      {styles.label}
    </span>
  );
};

export const Sessions = () => {
  const { fetchAuthorized } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [toast,    setToast]    = useState(null);
  const [busy,     setBusy]     = useState(null); // id сессии, по которой идёт revoke

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(() => {
    setLoading(true);
    fetchAuthorized('/api/dashboard/sessions')
      .then(r => r.json())
      .then(d => { if (d.success) setSessions(d.sessions || []); })
      .catch(() => showToast('Ошибка загрузки сессий', 'error'))
      .finally(() => setLoading(false));
  }, [fetchAuthorized]);

  useEffect(() => { load(); }, [load]);

  // Авто-обновление раз в 15 секунд: чтобы last_seen_at и список «живых» сессий
  // не отставали от реальности, пока админ смотрит на эту вкладку.
  useEffect(() => {
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [load]);

  const handleRevoke = async (session) => {
    const isCurrent = session.is_current;
    const msg = isCurrent
      ? 'Это ваша текущая сессия. Вы будете выкинуты из дашборда. Продолжить?'
      : `Завершить сессию «${session.label || '—'}» (${session.ip || 'без IP'})?`;
    if (!window.confirm(msg)) return;

    setBusy(session.id);
    try {
      const r = await fetchAuthorized(`/api/dashboard/sessions/${session.id}`, { method: 'DELETE' });
      const d = await r.json().catch(() => ({}));
      if (d.success) {
        showToast(isCurrent ? 'Сессия завершена. До свидания.' : 'Сессия отозвана', 'success');
        // Если это была наша сессия — следующий fetch вернёт 401 и AuthContext
        // выкинет нас на логин. До этого момента просто перезагружаем список.
        load();
      } else {
        showToast(d.error || 'Не удалось отозвать сессию', 'error');
      }
    } catch (e) {
      // Если выкинули себя — fetchAuthorized бросит 'Unauthorized', что ок.
      if (String(e.message) !== 'Unauthorized') {
        showToast('Ошибка сети', 'error');
      }
    } finally {
      setBusy(null);
    }
  };

  const activeCount = sessions.filter(s => s.status === 'active').length;

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-1)', marginBottom: '4px' }}>
            Сессии дашборда
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--text-3)' }}>
            Активных: <strong style={{ color: 'var(--text-1)' }}>{activeCount}</strong> · всего записей: {sessions.length} · окно 30 дней
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', borderRadius: '8px',
            border: '1px solid var(--border)', background: 'transparent',
            color: 'var(--text-2)', cursor: 'pointer', fontSize: '13px',
          }}
        >
          <RefreshCw size={14} />
          Обновить
        </button>
      </div>

      <div className="glass-card" style={{ overflow: 'hidden' }}>
        {loading && sessions.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>Загрузка...</div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
            Сессий пока нет.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Браузер / ОС', 'IP', 'Создана', 'Последняя активность', 'Статус', ''].map((h, i) => (
                  <th key={i} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', color: 'var(--text-3)', fontWeight: 600 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => {
                const canRevoke = s.status === 'active';
                return (
                  <tr key={s.id} style={{
                    borderBottom: '1px solid var(--border)',
                    opacity: s.status === 'active' ? 1 : 0.55,
                    background: s.is_current ? 'rgba(99,102,241,0.06)' : 'transparent',
                  }}>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-1)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Shield size={14} style={{ color: s.is_current ? 'var(--brand)' : 'var(--text-3)' }} />
                        <span>{s.label || '—'}</span>
                        {s.is_current && (
                          <span style={{
                            fontSize: '11px', padding: '1px 6px', borderRadius: '4px',
                            background: 'rgba(99,102,241,0.18)', color: 'var(--brand)', fontWeight: 600,
                          }}>
                            это вы
                          </span>
                        )}
                      </div>
                      {s.user_agent && (
                        <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', fontFamily: 'monospace', maxWidth: '480px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.user_agent}>
                          {s.user_agent}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-2)', fontFamily: 'monospace' }}>
                      {s.ip || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-2)' }}>
                      {fmtDate(s.created_at)}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text-2)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={12} style={{ color: 'var(--text-3)' }} />
                        {fmtDate(s.last_seen_at)}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <StatusBadge status={s.status} />
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      {canRevoke && (
                        <button
                          className="btn-icon"
                          title={s.is_current ? 'Выйти из текущей сессии' : 'Выкинуть эту сессию'}
                          disabled={busy === s.id}
                          onClick={() => handleRevoke(s)}
                          style={{ color: 'var(--red)' }}
                        >
                          <LogOut size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ marginTop: '16px', fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5 }}>
        Сессия создаётся при логине в дашборд. Если сессия не использовалась более 30 дней — она автоматически
        помечается как истёкшая и не пропускает запросы. Кнопка <LogOut size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> отзывает сессию немедленно
        и закрывает её WebSocket. Мастер-токен из <code>.env</code> можно сменить — он используется только
        при логине, активные сессии после смены продолжат работать до явного отзыва.
      </p>
    </div>
  );
};
